/** Scroll container for all cbta-web rating pages. */
export const TS1_RATING_PAGE_SCROLL_CONTAINER_ID = "cbta-web-page-container";

/** App header (64px) + Step 1 fixed stepper bar (~88px). */
export const TS1_RATING_STICKY_HEADER_OFFSET = 152;

/** Retries until a Step 3 anchor exists after a candidate switch or collapsed section. */
const STEP3_SCROLL_FIND_RETRY_DELAYS_MS = [0, 0, 0, 50, 120, 220, 360];
/** Later passes beat iPad Safari reverting a programmatic scroll after the tap. */
const STEP3_SCROLL_CORRECTION_DELAYS_MS = [50, 120, 300];
const STEP3_SCROLL_ALIGN_TOLERANCE_PX = 2;
const STEP3_SCROLL_EDGE_PADDING_PX = 12;

let step3ScrollGeneration = 0;
let step3ScrollTimers: number[] = [];

/** Review title row (40px) + candidate switcher (52px) — always pinned on Step 3. */
export const STEP3_REVIEW_STICKY_CHROME_HEIGHT = 92;

export function candidateHeaderElementId(index: number): string {
	return `ts1-candidate-header-${index}`;
}

export function computeScrollTopForCandidateHeader(
	container: Pick<HTMLElement, "scrollTop" | "getBoundingClientRect">,
	header: Pick<HTMLElement, "getBoundingClientRect">,
	stickyOffset = TS1_RATING_STICKY_HEADER_OFFSET,
): number {
	const containerRect = container.getBoundingClientRect();
	const headerRect = header.getBoundingClientRect();
	return Math.max(0, container.scrollTop + (headerRect.top - containerRect.top) - stickyOffset);
}

/** Scroll `#cbta-web-page-container` so the candidate accordion header is visible. */
export function scrollPageContainerToCandidateHeader(
	index: number,
	options?: { stickyOffset?: number; behavior?: ScrollBehavior },
): boolean {
	const container = document.getElementById(TS1_RATING_PAGE_SCROLL_CONTAINER_ID);
	const header = document.getElementById(candidateHeaderElementId(index));
	if (!container || !header) return false;

	const top = computeScrollTopForCandidateHeader(container, header, options?.stickyOffset);
	container.scrollTo({ top, behavior: options?.behavior ?? "smooth" });
	return true;
}

/** Retry scroll until the header exists in the DOM (post-render / post-draft-save). */
export function scrollPageContainerToCandidateHeaderWhenReady(
	index: number,
	maxAttempts = 12,
): void {
	let attempt = 0;

	const tryScroll = (): void => {
		if (scrollPageContainerToCandidateHeader(index)) return;
		if (++attempt >= maxAttempts) return;
		requestAnimationFrame(tryScroll);
	};

	requestAnimationFrame(tryScroll);
}

/** Scroll `#cbta-web-page-container` so a target element is visible below the sticky header. */
export function scrollPageContainerToElementId(
	elementId: string,
	options?: { stickyOffset?: number; behavior?: ScrollBehavior },
): boolean {
	const container = document.getElementById(TS1_RATING_PAGE_SCROLL_CONTAINER_ID);
	const target = document.getElementById(elementId);
	if (!container || !target) return false;

	const top = computeScrollTopForCandidateHeader(container, target, options?.stickyOffset);
	container.scrollTo({ top, behavior: options?.behavior ?? "smooth" });
	return true;
}

/** Retry scroll until the target element exists in the DOM. */
export function scrollPageContainerToElementIdWhenReady(
	elementId: string,
	maxAttempts = 12,
): void {
	let attempt = 0;

	const tryScroll = (): void => {
		if (scrollPageContainerToElementId(elementId)) return;
		if (++attempt >= maxAttempts) return;
		requestAnimationFrame(tryScroll);
	};

	requestAnimationFrame(tryScroll);
}

/** Overlap of the fixed rating header into the page scroll container viewport. */
export function measureFixedRatingHeaderOverlap(containerTop: number): number {
	const fixedHeader = document.querySelector("#rating-form .ts1-rating-form-v3__fixed-header");
	if (!fixedHeader) return 0;
	return Math.max(0, fixedHeader.getBoundingClientRect().bottom - containerTop);
}

/** Top inset for Step 3 targets: fixed form header + candidate switcher chrome. */
export function measureStep3ReviewScrollStickyOffset(): number {
	const container = document.getElementById(TS1_RATING_PAGE_SCROLL_CONTAINER_ID);
	if (!container) return TS1_RATING_STICKY_HEADER_OFFSET;

	const containerTop = container.getBoundingClientRect().top;
	const padding = 16;
	let obscuringBottom = containerTop + padding;

	const fixedHeader = document.querySelector("#rating-form .ts1-rating-form-v3__fixed-header");
	if (fixedHeader) {
		obscuringBottom = Math.max(obscuringBottom, fixedHeader.getBoundingClientRect().bottom + 8);
	}

	const step3Chrome = document.querySelector("#rating-form app-ts1-rating-step3-review .ts1-review__sticky-chrome");
	if (step3Chrome) {
		const chromeRect = step3Chrome.getBoundingClientRect();
		const chromeHeight = Math.max(STEP3_REVIEW_STICKY_CHROME_HEIGHT, chromeRect.height);
		const headerOverlap = measureFixedRatingHeaderOverlap(containerTop);
		// Candidate header is sticky on Step 3 — always reserve its height below the form header.
		const pinnedChromeBottom = containerTop + headerOverlap + chromeHeight;
		obscuringBottom = Math.max(obscuringBottom, chromeRect.bottom, pinnedChromeBottom);
	}

	return Math.round(obscuringBottom - containerTop);
}

export function findStep3ReviewScrollTarget(elementId: string): HTMLElement | null {
	const scoped = document.querySelector(
		`#rating-form app-ts1-rating-step3-review #${CSS.escape(elementId)}`,
	);
	if (scoped instanceof HTMLElement) return scoped;

	const fallback = document.getElementById(elementId);
	return fallback instanceof HTMLElement ? fallback : null;
}

function step3ReviewRevealDelta(
	container: HTMLElement,
	target: HTMLElement,
	stickyOffset: number,
): number {
	const containerRect = container.getBoundingClientRect();
	const targetRect = target.getBoundingClientRect();
	const minTop = containerRect.top + stickyOffset + STEP3_SCROLL_EDGE_PADDING_PX;
	return targetRect.top - minTop;
}

function clearStep3ScrollTimers(): void {
	for (const timer of step3ScrollTimers) {
		window.clearTimeout(timer);
	}
	step3ScrollTimers = [];
}

function scheduleStep3Scroll(callback: () => void, delay: number): void {
	const timer = window.setTimeout(callback, delay);
	step3ScrollTimers.push(timer);
}

/** Nudge the page container so the target sits just under the sticky header. */
export function nudgePageContainerToRevealTarget(
	container: HTMLElement,
	target: HTMLElement,
	options?: { stickyOffset?: number; bottomClearance?: number },
): void {
	const stickyOffset = options?.stickyOffset ?? measureStep3ReviewScrollStickyOffset();
	const delta = step3ReviewRevealDelta(container, target, stickyOffset);

	// One write. A sync scrollBy loop re-reads a stale rect on iPad and overshoots,
	// and scrollBy during a tap is often dropped on that scroller.
	if (Math.abs(delta) < STEP3_SCROLL_ALIGN_TOLERANCE_PX) return;
	container.scrollTop += delta;
}

/** Scroll a Step 3 review anchor into view and correct for fixed/sticky overlays. */
export function revealStep3ReviewTarget(
	elementId: string,
	options?: { stickyOffset?: number; bottomClearance?: number },
): boolean {
	const container = document.getElementById(TS1_RATING_PAGE_SCROLL_CONTAINER_ID);
	const target = findStep3ReviewScrollTarget(elementId);
	if (!container || !target || target.getBoundingClientRect().height < 1) return false;

	nudgePageContainerToRevealTarget(container, target, options);
	return true;
}

/** Retry Step 3 review scroll until the target exists, then correct after the tap. */
export function scrollPageContainerToStep3ReviewTargetWhenReady(elementId: string): void {
	const generation = ++step3ScrollGeneration;
	clearStep3ScrollTimers();
	let findAttempt = 0;

	const isCurrent = (): boolean => generation === step3ScrollGeneration;

	const correct = (): void => {
		if (!isCurrent()) return;
		revealStep3ReviewTarget(elementId);
	};

	const scheduleCorrections = (): void => {
		requestAnimationFrame(() => {
			if (!isCurrent()) return;
			correct();
			requestAnimationFrame(correct);
		});
		for (const delay of STEP3_SCROLL_CORRECTION_DELAYS_MS) {
			scheduleStep3Scroll(() => {
				if (!isCurrent()) return;
				correct();
			}, delay);
		}
	};

	const findTarget = (): void => {
		if (!isCurrent()) return;

		const container = document.getElementById(TS1_RATING_PAGE_SCROLL_CONTAINER_ID);
		const target = findStep3ReviewScrollTarget(elementId);
		if (container && target && target.getBoundingClientRect().height >= 1) {
			correct();
			scheduleCorrections();
			return;
		}

		findAttempt++;
		if (findAttempt >= STEP3_SCROLL_FIND_RETRY_DELAYS_MS.length) return;

		const delay = STEP3_SCROLL_FIND_RETRY_DELAYS_MS[findAttempt];
		if (delay === 0) {
			requestAnimationFrame(findTarget);
		} else {
			scheduleStep3Scroll(findTarget, delay);
		}
	};

	requestAnimationFrame(findTarget);
}

/** @deprecated Use revealStep3ReviewTarget — kept for tests and legacy callers. */
export function scrollPageContainerToElement(
	target: HTMLElement,
	container: HTMLElement,
	options?: {
		stickyOffset?: number;
		bottomClearance?: number;
		behavior?: ScrollBehavior;
	},
): boolean {
	nudgePageContainerToRevealTarget(container, target, options);
	return true;
}

/** @deprecated Use revealStep3ReviewTarget — kept for tests and legacy callers. */
export function scrollPageContainerToStep3ReviewTarget(
	elementId: string,
	options?: {
		stickyOffset?: number;
		bottomClearance?: number;
		behavior?: ScrollBehavior;
	},
): boolean {
	const container = document.getElementById(TS1_RATING_PAGE_SCROLL_CONTAINER_ID);
	const target = findStep3ReviewScrollTarget(elementId);
	if (!container || !target || target.getBoundingClientRect().height < 1) return false;

	nudgePageContainerToRevealTarget(container, target, options);
	return true;
}

/** Build a stable DOM id for a Step 3 review scroll target. */
export function step3ReviewScrollTargetId(section: string, field?: string): string {
	if (field) return `ts1-review-${section}-${field}`;
	return `ts1-review-section-${section}`;
}
